import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import { NOINDEX_ROUTES, PAGE_META } from '../../seo/pageMeta'
import type { PublicMarketingRoute } from '../../seo/routes'
import { PUBLIC_MARKETING_ROUTES } from '../../seo/routes'
import { OG_IMAGE_PATH, SITE_NAME, absoluteUrl } from '../../seo/site'

const PUBLIC_ROUTE_SET = new Set<string>(PUBLIC_MARKETING_ROUTES)

function isPublicMarketingRoute(path: string): path is PublicMarketingRoute {
  return PUBLIC_ROUTE_SET.has(path)
}

interface PageSEOProps {
  title?: string
  description?: string
  path?: string
  noindex?: boolean
  jsonLd?: object | object[]
}

export function PageSEO({ title, description, path, noindex, jsonLd }: PageSEOProps) {
  const location = useLocation()
  const routePath = path ?? location.pathname
  const routeMeta = isPublicMarketingRoute(routePath) ? PAGE_META[routePath] : undefined
  const resolvedTitle = title ?? routeMeta?.title ?? `${SITE_NAME} — Uptime monitoring`
  const resolvedDescription =
    description ??
    routeMeta?.description ??
    'Monitor your websites and get Slack alerts when something goes wrong.'
  const canonical = absoluteUrl(routePath)
  const ogImage = absoluteUrl(OG_IMAGE_PATH)
  const shouldNoindex =
    noindex ??
    (NOINDEX_ROUTES.has(routePath) ||
      routePath.startsWith('/admin') ||
      routePath.startsWith('/monitors') ||
      routePath.startsWith('/escalation-policies') ||
      routePath.startsWith('/status-pages') ||
      /^\/status\/[^/]+/.test(routePath))

  const jsonLdItems = jsonLd == null ? [] : Array.isArray(jsonLd) ? jsonLd : [jsonLd]

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={canonical} />
      {shouldNoindex ? <meta name="robots" content="noindex, nofollow" /> : null}

      <meta property="og:title" content={resolvedTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLdItems.map((item, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  )
}
